import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SeoHelmet from '@/components/base/SeoHelmet';
import { categories } from '@/mocks/categories';
import { articleMap, getLatestArticles } from '@/mocks/articles';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleMap[slug] : undefined;

  if (!article) {
    return <NotFound />;
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = getLatestArticles(4).filter((a) => a.slug !== article.slug).slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '免费91 网页',
      logo: {
        '@type': 'ImageObject',
        url: 'https://free91.com/logo.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://free91.com/article/${article.slug}`,
    },
  };

  return (
    <>
      <SeoHelmet
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://free91.com/article/${article.slug}`}
        ogType="article"
        ogImage={article.image}
        schema={articleSchema}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category?.name || '分类', href: category ? `/category/${category.slug}` : undefined },
              { label: article.title },
            ]}
          />
        </div>

        <article className="max-w-4xl mx-auto px-4 md:px-6 pb-14 md:pb-20">
          {/* Article Header */}
          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              {category && (
                <Link
                  to={`/category/${category.slug}`}
                  className="text-xs px-3 py-1 rounded-full bg-primary-100 text-primary-700 font-medium hover:bg-primary-200 transition-colors"
                >
                  {category.name}
                </Link>
              )}
              {article.featured && (
                <span className="text-xs px-3 py-1 rounded-full bg-accent-100 text-accent-700 font-medium">
                  精选推荐
                </span>
              )}
            </div>

            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-3 mt-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                阅读约{article.readTime}
              </span>
            </div>

            <div className="mt-4 flex flex-wrap gap-1.5">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 border border-background-200/70"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </header>

          {/* Featured Image */}
          <div className="rounded-xl overflow-hidden mb-8">
            <img
              src={article.image}
              alt={article.title}
              title={`${article.title} - 免费91 网页`}
              className="w-full aspect-video object-cover object-top"
            />
          </div>

          {/* Article Content */}
          <div
            className="prose prose-sm md:prose-base max-w-none
              prose-headings:text-foreground-950 prose-headings:font-heading
              prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:text-sm
              prose-strong:text-foreground-900
              prose-a:text-primary-600 prose-a:no-underline hover:prose-a:underline
              prose-li:text-foreground-700 prose-li:text-sm
              prose-h2:text-xl prose-h2:font-bold prose-h2:mt-8 prose-h2:mb-4
              prose-h3:text-lg prose-h3:font-semibold prose-h3:mt-6 prose-h3:mb-3"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Article Footer */}
          <div className="mt-10 pt-6 border-t border-background-200/70">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500">分享到：</span>
                <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微信">
                  <i className="ri-wechat-line text-sm"></i>
                </button>
                <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微博">
                  <i className="ri-weibo-line text-sm"></i>
                </button>
                <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="复制链接">
                  <i className="ri-link text-sm"></i>
                </button>
              </div>
              <Link
                to={category ? `/category/${category.slug}` : '/'}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
              >
                返回{category?.name || '首页'}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-10 md:py-14">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h3 className="text-lg font-bold font-heading text-foreground-950 mb-5">相关推荐</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-300 transition-all duration-300"
                  >
                    <div className="h-36 overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-3">
                      <h4 className="text-xs font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
                        {ra.title}
                      </h4>
                      <p className="text-xs text-foreground-400 mt-1">{ra.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}